// Libs
import { useQuery } from '@apollo/client';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

// Local components
import AdminModal from '../AdminBinder/AdminContentContainer/TabContent/ListContainer/AdminModal';
import InspectionTypeActionbar from './InspectionTypeActionbar';
import InspectionTypeCardList from './InspectionTypeCardList';
import InspectionTypeForm from './InspectionTypeForm';
import InspectionTypeTable from './InspectionTypeTable';
import InspectionTypeView from './InspectionTypeView';

// Queries
import {
  GET_ALL_GLOBAL_INSPECTION_TEMPLATES,
  GET_ALL_MARKETS,
  GET_ALL_WORKFLOW_GLOBAL_TEMPLATES,
  GET_ROLES_QUERY_GLOBAL
} from '../../api/queries/queries_local';

// Utils
import useCheckDocument from 'Shared/Utils/useCheckDocument';
import {
  SORTING_VARIANTS,
  VIEW_LIST_VARIANTS
} from './InspectionTypeActionbar';

// Transforms
import { iBinderHeaderEnum, Region } from '@ibinder/ui-common-models';
import {
  useFeatureToggle,
  useIBTranslation,
  useModalAction
} from '@ibinder/ui-common-hooks';

import { FILTERED_WORKFLOW_TEMPLATES } from '@ibinder/ui-common-utils';
import {
  getGroupsFromAccessor,
  getHeaders,
  mapQueryToTableData,
  sortTemplates,
  transformGlobalInspectionTemplateList
} from './inspectionTypeTransformations';
import CreateInspectionTemplateSideSheet from './NewInspectionSideSheet/CreateInspectionTemplateSideSheet';
import { getNewInspectionTemplateLabels } from './NewInspectionSideSheet/translationHelpers';

// Styles
const ModalWrapper = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export enum FORM_MODES {
  CREATE = 'create',
  EDIT = 'edit',
  DUPLICATE = 'duplicate'
}

interface Props {
  isCompliant?: boolean;
}

const InspectionTypeTemplates = ({ isCompliant = false }: Props) => {
  const { t } = useIBTranslation();

  const { isEnabled: canUseNewInspectionTemplate } = useFeatureToggle(
    'Configcat_newInspectionTemplate'
  );

  const { isEnabled: usePrefixIdForIssues } = useFeatureToggle(
    'Configcat_PrefixIdForIssues'
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const showCreateModalNode = useRef();
  const [formMode, setFormMode] = useState<FORM_MODES>();
  const createInspectionTypeTemplate = () => {
    setFormMode(FORM_MODES.CREATE);
    setShowCreateModal(true);
    if (canUseNewInspectionTemplate) openInspectionTemplateSideSheet();
  };

  const hideCreateModalNode = () => {
    setShowCreateModal(false);
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const showEditModalNode = useRef();

  const editInspectionTypeTemplate = () => {
    setFormMode(FORM_MODES.EDIT);
    setShowEditModal(true);
    openInspectionTemplateSideSheet();
  };

  const hideEditModalNode = () => {
    setShowEditModal(false);
  };

  const [showViewModal, setShowViewModal] = useState(null);
  const showViewModalNode = useRef();

  const hideViewModal = () => {
    setShowViewModal(null);
  };

  const [activeView, setActiveView] = useState(VIEW_LIST_VARIANTS[0]);
  const changeActiveListView = (index) => {
    setActiveView(VIEW_LIST_VARIANTS[index]);
  };

  function getRegionHeaders() {
    if (isCompliant)
      return {
        [iBinderHeaderEnum.TENANTID]: '', // Needs to be empty string to override automatic set tenantId
        [iBinderHeaderEnum.REGION]: Region.Eu
      };

    return {
      [iBinderHeaderEnum.REGION]: Region.International
    };
  }

  const marketInfo = useQuery(GET_ALL_MARKETS, {
    fetchPolicy: 'no-cache',
    context: getRegionHeaders()
  });

  const workflowTemplates = useQuery(GET_ALL_WORKFLOW_GLOBAL_TEMPLATES, {
    fetchPolicy: 'no-cache',
  }
  );
  const templateList = workflowTemplates?.data?.get;
  const globalWorkflowTemplates = templateList?.filter(template => !FILTERED_WORKFLOW_TEMPLATES.includes(template.name));
  let workflowTemplateList = [];
  if(globalWorkflowTemplates){
    workflowTemplateList = [
      { Key: 'noTemplate', Value: t('noTemplate'),tenantSpecific: 'noTemplate' },
      ...globalWorkflowTemplates
        ?.map((templates) => ({
          Key: templates.id,
          Value: t(templates.name),
          tenantSpecific: templates.tenantSpecific
        }))
        .sort((a, b) => a.Value.localeCompare(b.Value))
    ];
  }

  const globalTemplates = useQuery(GET_ALL_GLOBAL_INSPECTION_TEMPLATES, {
    context: {
      headers: getRegionHeaders()
    },
    fetchPolicy: 'no-cache',
    skip: !marketInfo.data
  });

  const onActionSelect = (action, itemId: string) => {
    switch (action) {
      case 'view':
        //@ts-ignore Typing makes no sense to me?
        setSelectedIds([itemId]);
        setShowViewModal(itemId);
        setFormMode(FORM_MODES.EDIT);
        openInspectionTemplateSideSheet();
        return;
      case 'duplicate':
        //@ts-ignore Typing makes no sense to me?
        setSelectedIds([itemId]);
        setFormMode(FORM_MODES.DUPLICATE);
        setShowEditModal(true);
        return;
      case 'edit':
        //@ts-ignore Typing makes no sense to me?
        setSelectedIds([itemId]);
        setFormMode(FORM_MODES.EDIT);
        setShowEditModal(true);

        if (canUseNewInspectionTemplate) openInspectionTemplateSideSheet();
        return;
    }
  };

  const headers = getHeaders(t, onActionSelect, workflowTemplateList);

  const [
    selectedIds,
    setSelectedIds,
    selectItem,
    selectItems,
    groupCheckStatus
  ] = useCheckDocument();

  const [sortingFilter, setSortingFilter] = useState(SORTING_VARIANTS.DESC);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sortBy, setSortBy] = useState({ id: 'Name', desc: true });

  const changeSorting = () => {
    if (sortingFilter === SORTING_VARIANTS.ASC) {
      setSortingFilter(SORTING_VARIANTS.DESC);
      setSortBy({ ...sortBy, desc: true });
    } else if (sortingFilter === SORTING_VARIANTS.DESC) {
      setSortingFilter(SORTING_VARIANTS.ASC);
      setSortBy({ ...sortBy, desc: false });
    }
  };

  const changeActiveSortField = (index) => {
    setActiveIndex(index);
    setSortingFilter(SORTING_VARIANTS.DESC);
    setSortBy({ id: headers[index].id, desc: true });
  };

  const onSort = (e) => {
    const index = headers.findIndex(({ id }) => id === e.id);
    setActiveIndex(index);
    setSortingFilter(
      e.desc === true ? SORTING_VARIANTS.DESC : SORTING_VARIANTS.ASC
    );
    setSortBy({ id: headers[index].id, desc: e.desc === true });
  };

  const sortedTemplates = globalTemplates?.data?.globalInspectionType
    ? sortTemplates(
        transformGlobalInspectionTemplateList(
          mapQueryToTableData(
            globalTemplates?.data?.globalInspectionType,
            marketInfo?.data?.get,
          )
        ),
        headers[activeIndex].propName,
        sortingFilter === SORTING_VARIANTS.DESC
      )
    : [];

  const groupBy = {
    accessor: 'Category',
    groupByColumns: getGroupsFromAccessor(sortedTemplates, 'category')
  };

  const selectedInspectionTemplate =
    globalTemplates.data?.globalInspectionType?.find(
      ({ id }) => id === selectedIds[0]
    );

  const GLOBAL_ENTREPRENEUR = 'a263f198-7a6d-452a-bb7a-71dab439367c';
  const contractors = useQuery(GET_ROLES_QUERY_GLOBAL, {
    variables: {
      filterbyRoleTypeId: GLOBAL_ENTREPRENEUR,
      includeDisabled: false
    }
  });

  const [InspectionTemplateSideSheet, openInspectionTemplateSideSheet] =
    useModalAction({}, (props) => (
      <CreateInspectionTemplateSideSheet
        labels={getNewInspectionTemplateLabels(t)}
        {...{
          ...props,
          onClose: () => {
            props.onClose();
          },
          isCompliant,
          setMode: setFormMode,
          inspectionType: selectedInspectionTemplate,
          mode: formMode,
          market: selectedInspectionTemplate?.market,
          marketList: marketInfo.data?.get,
          contractorList: contractors.data?.get,
          workflowTemplateList,
          usePrefixIdForIssues
        }}
      />
    ));

  return (
    <>
      {canUseNewInspectionTemplate ? (
        InspectionTemplateSideSheet
      ) : (
        <>
          {showCreateModal && (
            <AdminModal
              ref={showCreateModalNode}
              onClose={hideCreateModalNode}
              label={t('Create new inspection type')}
              name={'Inspection template'}
              // @ts-ignore
              Component={({ onClose }) => (
                <ModalWrapper>
                  <InspectionTypeForm
                    onClose={hideCreateModalNode}
                    isCompliant={isCompliant}
                  />
                </ModalWrapper>
              )}
            />
          )}
          {showEditModal && selectedIds.length === 1 && (
            <AdminModal
              ref={showEditModalNode}
              onClose={hideEditModalNode}
              label={t('Edit inspection type')}
              name={'Inspection template'}
              // @ts-ignore
              Component={({ onClose }) => (
                <ModalWrapper>
                  <InspectionTypeForm
                    onClose={() => {
                      setFormMode(null);
                      hideEditModalNode();
                    }}
                    inspectionType={globalTemplates.data.globalInspectionType.find(
                      ({ id }) => id === selectedIds[0]
                    )}
                    mode={formMode}
                    isCompliant={isCompliant}
                  />
                </ModalWrapper>
              )}
            />
          )}
          {!!showViewModal && (
            <AdminModal
              ref={showViewModalNode}
              onClose={hideViewModal}
              label={t('viewInspectionType')}
              name={t('inspectionTemplateName')}
              // @ts-ignore
              Component={({ onClose }) => (
                <ModalWrapper>
                  <InspectionTypeView
                    onClose={hideViewModal}
                    inspectionType={sortedTemplates.find(
                      ({ id }) => id === showViewModal
                    )}
                    onStartEdit={(id) => {
                      hideViewModal();
                      onActionSelect(FORM_MODES.EDIT, id);
                    }}
                    onStartDuplicate={(id) => {
                      hideViewModal();
                      onActionSelect(FORM_MODES.DUPLICATE, id);
                    }}
                  />
                </ModalWrapper>
              )}
            />
          )}
        </>
      )}

      <InspectionTypeActionbar
        {...{
          headers,
          queryData: sortedTemplates,
          selectedIds,
          activeView,
          changeActiveListView,
          changeSorting,
          changeActiveSortField,
          sortingFilter,
          createInspectionTypeTemplate,
          editInspectionTypeTemplate,
          activeIndex
        }}
      />
      {sortedTemplates.length > 0 && activeView.index === 0 && (
        <InspectionTypeTable
          {...{
            headers,
            queryData: sortedTemplates,
            selectedIds,
            setSelectedIds,
            selectItem,
            selectItems,
            groupCheckStatus,
            sortBy,
            onSort,
            groupBy
          }}
        />
      )}
      {sortedTemplates.length > 0 && activeView.index === 1 && (
        <InspectionTypeCardList
          {...{
            headers,
            queryData: sortedTemplates,
            selectedIds,
            setSelectedIds,
            selectItem,
            selectItems,
            groupCheckStatus,
            sortBy,
            onSort,
            groupBy,
            onActionSelect,
            workflowTemplateList
          }}
        />
      )}
    </>
  );
};

export default InspectionTypeTemplates;
